import React, { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import { format } from 'date-fns';
import useFetch from 'use-http';
import moment from 'moment';
import { useUserState } from '@/components/context/UserContext';
import { DownloadButton } from '@/components/ui/shared/DownloadButton';
import { InfoButton } from '@/components/ui/shared/InfoButton';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogBody,
} from '@/components/ui/dialog';
import { Ban, Check } from 'lucide-react';
import Modal from '@/components/shared/Modal';

export default function SignedConsentsTable() {
  const { get: getConsents } = useFetch(`/ConsentFrom/ConsentFroms`);
  const { get: getConsentsFromDetails } = useFetch(
    `/ConsentFrom/ConsentFromsDetails`,
  );
  const { get: getFile } = useFetch('/UserAccount/DownloadAmisFile');
  const { post: sendConsentLog } = useFetch(`/Logs/ConsentForm/Modify/Init`);
  const userState = useUserState();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isStartOpen, setIsStartOpen] = useState(false);

  const [showSignModal, setShowSignModal] = useState(false);
  const [signRequestUrl, setSignRequestUrl] = useState(null);

  const [
    consentSigningAdditionalInformation,
    setConsentSigningAdditionalInformation,
  ] = useState(false);
  const [consentSigningSelected, setConsentSigningSelected] = useState(null);

  const [initialFilters, setInitialFilters] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const queryString = userState?.espbiEsi
        ? `?espbiEsi=${userState?.espbiEsi}`
        : '';

      const consents = await getConsents(queryString);

      if (consents) {
        const filtered = consents.filter(
          (x) => x.status !== 'LaukiamaIsankstinioPasirasymo',
        );

        const mapped = filtered.map((item) => {
          const isFilled = item.status !== 'LaukiamaIsankstinioUzpildymo';

          return {
            id: item.id,
            title: item.name,
            date: item.date,
            status: item.status,
            filledStatus: isFilled ? 'filled' : 'unfilled',
          };
        });

        setData(mapped);

        const shouldStartOpen = filtered.some(
          (x) => x.status === 'LaukiamaIsankstinioUzpildymo',
        );
        setIsStartOpen(shouldStartOpen);

        const hasAwaitingPrefill = filtered.some(
          (x) => x.status === 'LaukiamaIsankstinioUzpildymo',
        );

        setInitialFilters([
          {
            id: 'filledStatus',
            value: hasAwaitingPrefill ? 'unfilled' : 'filled',
          },
        ]);
      } else {
        setData([]);
        setInitialFilters([{ id: 'filledStatus', value: 'filled' }]);
        setIsStartOpen(false);
      }

      setIsLoading(false);
    };
    if (isStartOpen) {
      fetchData();
    }
  }, [userState?.espbiEsi, getConsents, isStartOpen]);

  const base64toBlob = (base64Data, contentType = '') => {
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);
      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  const handleDownload = async (row) => {
    const details = await getConsentsFromDetails(`/${row.id}`);
    const file = details?.file;
    if (file) {
      const base64Data = await getFile(
        `?fileType=${file?.type}&fileGuid=${file?.guid}&category=AgreementsSoT`,
      );
      const blob = base64toBlob(base64Data, 'application/pdf');
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `${row.title} ${moment(new Date(row.date)).format(
        'YYYY-MM-DD',
      )}.pdf`;
      document.body.appendChild(downloadLink);
      setTimeout(() => {
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }, 120);
    }
  };

  const sendConsentFormLogToApi = (consent) => {
    sendConsentLog('', {
      id: consent.id,
      name: consent.title,
    });
  };

  const consentSelected = async (s) => {
    let query = `/${s.id}`;
    if (userState?.espbiEsi) {
      query += `?espbiEsi=${userState.espbiEsi}`;
    }
    const res = await getConsentsFromDetails(query);
    if (res?.signRequestUrl) {
      setSignRequestUrl(res.signRequestUrl);
      sendConsentFormLogToApi(s);
      // setShowSignModal(true);
      setIsStartOpen(false);
      window.open(res.signRequestUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const continueWithConsentSelected = async (s) => {
    const popupSetting = localStorage.getItem('consent-signing-popup');
    const expirationDate = popupSetting ? new Date(popupSetting) : null;

    if (popupSetting && expirationDate && expirationDate > new Date()) {
      await consentSelected(s);
    } else {
      const hours = 24;
      localStorage.setItem(
        'consent-signing-popup',
        new moment().add(hours, 'hours').toDate(),
      );
      setConsentSigningSelected(s);
      setConsentSigningAdditionalInformation(true);
    }
  };

  return (
    <>
      <DataTable
        key={`signed-consents-${isStartOpen ? 'open' : 'collapsed'}`}
        title="Sutikimai"
        columns={columns(handleDownload, continueWithConsentSelected)}
        loading={isLoading}
        data={data}
        collapsible={true}
        isOpen={isStartOpen}
        onOpenChange={(nextOpen) => {
          setIsStartOpen(nextOpen);
          console.log('DataTable is now', nextOpen ? 'open' : 'collapsed');
        }}
        initialColumnFilters={initialFilters}
        facetedFilters={[
          {
            columnId: 'filledStatus',
            title: 'Būsena',
            options: [
              { value: 'filled', label: 'Užpildyti', icon: Check },
              { value: 'unfilled', label: 'Neužpildyti', icon: Ban },
            ],
          },
        ]}
      />

      <Dialog
        open={consentSigningAdditionalInformation}
        onOpenChange={setConsentSigningAdditionalInformation}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Informacija prieš pildymą</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <DialogDescription asChild>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>
                  Man žinoma, kad pacientas elektroniniu būdu pildomus
                  dokumentus taip pat turi teisę užpildyti atvykęs į padalinį
                  (atsisakyti pildyti elektroninį dokumentą, nutraukti jo
                  pildymą ir pan.).
                </p>
                <p>
                  Nepriklausomai nuo to, ar užpildytas elektroninis dokumentas
                  buvo pateiktas ar ne, pacientas atvykęs į padalinį turi teisę
                  pateikti mūsų specialistui bet kokius jam rūpimus klausimus,
                  susijusius su gaunamomis paslaugomis, ir gauti su tuo
                  susijusius paaiškinimus.
                </p>
              </div>
            </DialogDescription>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Grįžti</Button>
            </DialogClose>
            <Button
              onClick={async () => {
                await consentSelected(consentSigningSelected);
                setConsentSigningAdditionalInformation(false);
              }}
            >
              Tęsti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* <Dialog
        open={showSignModal}
        onOpenChange={(isOpen) => {
          setShowSignModal(isOpen);
          if (!isOpen) {
            window.location.reload();
          }
        }}
      >
        <DialogContent
          className="w-full max-w-full sm:max-w-180 max-h-screen flex flex-col p-0 z-[99999999] touch-auto !overflow-auto"
          style={{ touchAction: 'auto' }}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Sutikimo forma</DialogTitle>
          </DialogHeader>

          <iframe
            src={signRequestUrl || undefined}
            title="Consent Signing"
            allow="camera"
            className="w-full flex-1 border-0"
            style={{ touchAction: 'pan-x pan-y' }}
          />
          <DialogFooter className="hidden sm:flex p-4 border-t">
            <DialogClose asChild>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                Uždaryti
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog> */}

      <Modal
        visible={showSignModal}
        onClose={() => {
          setShowSignModal(false);
          window.location.reload();
          setSignRequestUrl(undefined);
        }}
        isGrey={true}
        fullScreen={true}
      >
        <iframe
          style={{ height: '40%', width: '40%' }}
          id="sontIframe"
          src={signRequestUrl}
          allow="camera"
        />

        <Modal.Actions>
          <Button
            size="large"
            onClick={async () => {
              window.location.reload();
            }}
          >
            Uždaryti
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}

function mapStatus(status) {
  switch (status) {
    case 'LaukiamaIsankstinioUzpildymo':
      return 'Laukiama užpildymo';
    case 'LaukiamaIsankstinioPasirasymo':
      return 'Laukiama parašo';
    case 'UzpildytasIsankstinisSutikimas':
      return 'Užpildytas';
    case 'Pasirasytas':
      return 'Pasirašytas';
    case 'Patvirtintas':
      return 'Patvirtintas';
    default:
      return 'Laukiama';
  }
}

const capitalizeFirstLetter = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const columns = (handleDownload, continueWithConsentSelected) => [
  {
    accessorKey: 'filledStatus',
    header: () => null,
    cell: () => null,
    enableSorting: false,
    enableHiding: true,
    enableFiltering: true,
    filterFn: (row, columnId, filterValue) => {
      const value = row.getValue(columnId);
      if (Array.isArray(filterValue)) return filterValue.includes(value);
      return value === filterValue;
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const status = row.original.status;
      return status === 'Patvirtintas' ? (
        <DownloadButton onClick={() => handleDownload(row.original)} />
      ) : status === 'LaukiamaIsankstinioUzpildymo' ? (
        <InfoButton
          onClick={async () => await continueWithConsentSelected(row.original)}
        >
          Užpildyti
        </InfoButton>
      ) : null;
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'title',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="PAVADINIMAS" />
    ),
    cell: ({ row }) => (
      <span className="max-w-[500px] truncate font-medium ">
        {capitalizeFirstLetter(row.getValue('title'))}
      </span>
    ),
  },
  {
    accessorKey: 'date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => {
      const rawDate = row.getValue('date');
      const date = rawDate ? new Date(rawDate) : null;
      return (
        <span className="text-muted-foreground">
          {date ? format(date, 'yyyy-MM-dd') : '-'}
        </span>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="STATUSAS" />
    ),
    cell: ({ row }) => {
      const status = row.getValue('status');
      return (
        <Badge variant={status === 'Patvirtintas' ? 'success' : 'neutral'}>
          <span className="text-xs/4">{mapStatus(status)}</span>
        </Badge>
      );
    },
    enableSorting: true,
    enableFiltering: true,
  },
];
